import MlsDataConverter from '@/converters/2.6/MlsDataConverter';
import IAddress1UnitNoZipCode from '@/models/common/IAddress1UnitNoZipCode';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IMlsCsvFileInfo from '@/models/mls/IMlsCsvFileInfo';
import IMlsCsvFileInfos from '@/models/mls/IMlsCsvFileInfos';
import IMlsImportStats, { newMlsImportStats } from '@/models/mls/IMlsImportStats';
import IMLSPropertyV1 from '@/models/mls/IMLSPropertyV1';
import MlsPropertyDataUtils from '@/models/mls/MlsPropertyDataUtils';
import IMLSPropertySource from '@/models/mls/IMLSPropertySource';
import IMLSPropertyItem from '@/models/mls/IMLSPropertyItem';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import utils from '@/utils/QSUtils';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import IMLSPhotos from '@/models/mls/IMLSPhotos';

// Internal structures - payload types for actions
//
interface IPropertyItemIndexed {
    index: number;
    propertyItem: IMLSPropertyItem;
}

// Internal only: The set of mutation method names as strings.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    addProperty: 'addProperty',
    replaceProperty: 'replaceProperty',
    deleteProperty: 'deleteProperty',
    addCsvFileInfo: 'addCsvFileInfo',
};

/**
 * An MLS Store implemented using vuex-module-decorators.
 * https://github.com/championswimmer/vuex-module-decorators
 * https://championswimmer.in/vuex-module-decorators/pages/overview.html
 *
 * Getters at the top, Mutators at the bottom.
 * All Actions have a summary indicating they're an action (intellisense purposes).
 * All Actions are async since a Promise is implemented internally.
 */
@Module({ namespaced: true, name: 'mls' })
export default class MlsStoreModule extends VuexModule {
    // Private are the store properties
    private mlsProperties: IMLSPropertyData = [];
    private csvFileInfos: IMlsCsvFileInfos = [];
    private importStats: IMlsImportStats = newMlsImportStats();

    /**
     * Get the MLS property data
     */
    get MlsProperties(): IMLSPropertyData {
        return this.mlsProperties;
    }

    get MlsPropertyByAddress1UnitNoZipCode() {
        return (address1UnitNoZipCode: IAddress1UnitNoZipCode): IMLSPropertyItem | null => {
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            return propertyItem;
        };
    }

    get MlsPropertyByAddress() {
        return (compAddress: ICompAddress): IMLSPropertyItem | null => {
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            return propertyItem;
        };
    }

    get MlsPropertySourceByAddressAndFormFieldName() {
        return (compAddress: ICompAddress, fieldName: string): IMLSPropertySource[] | null | undefined => {
            let result: IMLSPropertySource[] | null | undefined;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsPropertyDataUtils.getPropertySourceByFormFieldName(propertyItem, fieldName);
            }
            return result;
        };
    }

    get MlsPropertySourceByAddressAndParentFormFieldName() {
        return (compAddress: ICompAddress, fieldName: string): IMLSPropertySource[] | null | undefined => {
            let result: IMLSPropertySource[] | null | undefined;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsPropertyDataUtils.getPropertySourceByParentFormFieldName(propertyItem, fieldName);
            }
            return result;
        };
    }

    /**
     * Attempts to get a MLS Property (in V1 format) using the given comp address.
     */
    get MlsPropertyFieldValueByAddress() {
        // This is how to implement a getter with arguments
        //
        return (compAddress: ICompAddress): IMLSPropertyV1 | null => {
            let result: IMLSPropertyV1 | null = null;

            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsDataConverter.convertSingleToV1(propertyItem);
            }
            return result;
        };
    }

    get MlsPropertyAdditionalPhotoCountByAddress() {
        return (compAddress: ICompAddress): number => {
            let result = 0;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem?.mediaUrls) {
                result = utils.objectSize(propertyItem.mediaUrls);
            }
            return result;
        };
    }

    get MlsPropertyAdditionalPhotosByAddress() {
        return (compAddress: ICompAddress): IMLSPhotos[] => {
            let result: any = [];
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem?.mediaUrls) {
                result = propertyItem.mediaUrls;
            }
            return result;
        };
    }

    /**
     * Gets the array of MLS CSV file infos.
     */
    get CsvFileInfos(): IMlsCsvFileInfos {
        return this.csvFileInfos;
    }

    /**
     * Get the latest import stats
     */
    get MlsImportStats(): IMlsImportStats {
        return this.importStats;
    }

    get HasProperties(): boolean {
        return this.mlsProperties.length > 0;
    }

    /**
     * Add or replace the given MLS property item to the store's MLS property data.
     * @param propertyItem
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceProperty(propertyItem: IMLSPropertyItem): Promise<boolean> {
        let result = false;
        const matchIdx = MlsPropertyDataUtils.getIndexByPropertyItem(this.mlsProperties, propertyItem);
        if (matchIdx >= 0) {
            const replacePayload: IPropertyItemIndexed = {
                index: Number(matchIdx),
                propertyItem: propertyItem,
            };
            this.context.commit(Mutate.replaceProperty, replacePayload);
        } else {
            this.context.commit(Mutate.addProperty, propertyItem);
            result = true;
        }
        return result;
    }

    @Action
    async removeProperty(propertyItem: IMLSPropertyItem): Promise<boolean> {
        let result = false;
        const matchIdx = MlsPropertyDataUtils.getIndexByPropertyItem(this.mlsProperties, propertyItem);
        if (matchIdx >= 0) {
            this.context.commit(Mutate.deleteProperty, matchIdx);
            result = true;
        }
        return result;
    }

    /**
     * Mutation:
     * @param propertyItem
     */
    @Mutation
    addProperty(propertyItem: IMLSPropertyItem) {
        this.mlsProperties.push(propertyItem);
    }

    /**
     * Mutation:
     * @param replacePayload
     */
    @Mutation
    replaceProperty(replacePayload: IPropertyItemIndexed) {
        this.mlsProperties[replacePayload.index] = replacePayload.propertyItem;
    }

    /**
     * Mutation:
     * @param index
     */
    @Mutation
    deleteProperty(index: number) {
        if (index < this.mlsProperties.length) {
            this.mlsProperties.splice(index, 1);
        }
    }

    /**
     * Mutation:
     * @param fileInfo
     */
    @Mutation
    addCsvFileInfo(fileInfo: IMlsCsvFileInfo) {
        this.csvFileInfos.push(fileInfo);
    }

    /**
     * Mutation:
     * @param fileInfo
     */
    @Mutation
    setImportStats(importStats: IMlsImportStats) {
        this.importStats = importStats;
    }

    /**
     * Mutation:
     */
    @Mutation
    resetState() {
        this.mlsProperties = [];
        this.csvFileInfos = [];
        this.importStats = newMlsImportStats();
    }
}
